/**
 * `/alerts` — the operator Queue.
 *
 * Answers exactly one question: what requires a decision NOW? Membership is
 * "status is Pending in this scope", full stop — not today, not this shift, not
 * within the global date picker. An alert raised yesterday is still queue work
 * while it is pending, and a reopened alert comes back.
 *
 * Pagination is the server's opaque cursor. The queue mutates under the
 * operator (rows leave as they are reviewed), and offset paging over a mutating
 * list silently skips rows — page 1 returns A,B; A is reviewed; offset 2 now
 * returns D and C is never seen.
 */
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { useGetAllLocations } from "@/locations/api/queries/location.queries.ts";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore.ts";
import { useAlertQueue } from "@/alerts/api/queries/alerts-workspace.queries";
import {
  ALERT_PAGE_SIZES,
  useAlertsWorkspaceStore,
} from "@/alerts/stores/use-alerts-workspace-store";
import { ALERT_TYPES, alertTypeLabel } from "@/alerts/types/machine-alert";
import { AlertMasterDetail } from "@/alerts/components/workspace/alert-master-detail";
import { AlertQueueSummaryBar } from "@/alerts/components/workspace/alert-queue-summary-bar";
import { AlertDetailPane } from "@/alerts/components/workspace/alert-detail-pane";
import { AlertsScopeGate } from "@/alerts/components/workspace/alerts-scope-gate";
import {
  MultiSelect,
  PageSizeSelect,
  ScopeSelect,
} from "@/alerts/components/workspace/alert-filter-controls";
import {
  QueueEmptyState,
  RefreshButton,
  RefreshingChip,
  ScopeRequiredState,
} from "@/alerts/components/workspace/alerts-list-states";
import type { AlertQueueFilterState } from "@/alerts/types/alerts-workspace";
import { useAlertsBreadcrumbs } from "@/alerts/hooks/useAlertsBreadcrumbs";

const AlertsQueuePage = () => {
  const { t } = useTranslation("alerts");
  const organizationId = useOrganizationStore((s) => s.selectedOrganizationId);
  const store = useAlertsWorkspaceStore();
  const locationsQuery = useGetAllLocations();
  const locations = useMemo(() => locationsQuery.data?.data ?? [], [locationsQuery.data]);

  // Zustand actions are defined once, so these deps are stable — the effects
  // react to the org / location list, not to every change of store state.
  const { syncOrganization, setQueueLocation, recoverEmptyQueuePage } = store;
  const { queueLocationId, queueCursor } = store;

  useEffect(() => {
    syncOrganization(organizationId);
  }, [organizationId, syncOrganization]);

  // Heal a scope the operator can no longer use. The location is persisted, so
  // it survives an org switch, a permission change or a renamed site — landing
  // on the first available location gets them to work instead of to an empty
  // selector they have to notice and fix.
  useEffect(() => {
    if (locations.length === 0) return;
    if (!locations.some((location) => location.id === queueLocationId)) {
      setQueueLocation(locations[0].id);
    }
  }, [locations, queueLocationId, setQueueLocation]);

  const selectedLocation = locations.find((l) => l.id === queueLocationId) ?? null;
  const machines = selectedLocation?.machines ?? [];

  // `setQueueLocation` is a Zustand action, so its identity is stable — which
  // the breadcrumb's `onSelect` requires (see useAlertsBreadcrumbs).
  useAlertsBreadcrumbs({ locationId: queueLocationId, onSelectLocation: setQueueLocation });

  // No location → no request: the contract requires one, and "every pending
  // alert everywhere" is not a scope the queue supports.
  const filterState: AlertQueueFilterState | null =
    queueLocationId == null
      ? null
      : {
          scope: { location_id: queueLocationId, machine_ids: store.queueMachineIds },
          type_in: store.queueTypes,
          include_preview: true,
          limit: store.queueLimit,
          cursor: queueCursor,
        };

  const query = useAlertQueue(filterState);
  const page = query.data;
  const rows = page?.items ?? [];

  // An empty page with NO navigation links means the cursor chain went stale —
  // the rows it pointed at were reviewed out from under us. A cursor is opaque,
  // so it can only be discarded, never repaired: fall back to the first page.
  // An empty page that still offers `next` is just a gap, not a stale chain.
  useEffect(() => {
    if (!query.isSuccess || queueCursor == null) return;
    if (rows.length === 0 && !page?.next && !page?.previous) recoverEmptyQueuePage();
  }, [query.isSuccess, rows.length, page?.next, page?.previous, queueCursor, recoverEmptyQueuePage]);

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
      <ScopeSelect
        label={t("filters.labels.location")}
        placeholder={t("filters.labels.locationPlaceholder")}
        value={queueLocationId}
        options={locations.map((location) => ({ value: location.id, label: location.name }))}
        onChange={setQueueLocation}
      />
      <MultiSelect
        label={t("filters.labels.machines")}
        values={store.queueMachineIds}
        options={machines.map((machine) => ({ value: machine.id, label: machine.name }))}
        onChange={store.setQueueMachines}
        disabled={queueLocationId == null}
      />
      <MultiSelect
        label={t("filters.labels.types")}
        values={store.queueTypes}
        options={ALERT_TYPES.map((type) => ({ value: type as string, label: alertTypeLabel(type) }))}
        onChange={store.setQueueTypes}
      />
      <div className="ml-auto flex items-center gap-3">
        <RefreshingChip show={query.isFetching && !query.isPending} />
        <RefreshButton isFetching={query.isFetching} onRefresh={() => void query.refetch()} />
        <PageSizeSelect
          value={store.queueLimit}
          options={ALERT_PAGE_SIZES}
          onChange={store.setQueueLimit}
        />
      </div>
    </div>
  );

  const footer = (
    <div className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground">
      <span className="tabular-nums">
        {/* Server count for the scope — the page is only a window onto it. */}
        {page
          ? t("queue.footer.shown", { shown: rows.length, total: page.count })
          : "—"}
      </span>
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2 text-xs"
          disabled={!page?.previous || query.isFetching}
          onClick={() => store.goToQueueCursor(page?.previous ?? null)}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          {t("pagination.previous")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2 text-xs"
          disabled={!page?.next || query.isFetching}
          onClick={() => store.goToQueueCursor(page?.next ?? null)}
        >
          {t("pagination.next")}
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );

  return (
    <AlertsScopeGate
      organizationId={organizationId}
      locations={locations}
      isLoading={locationsQuery.isPending}
      isError={locationsQuery.isError}
    >
      <div className="flex h-full min-h-0 flex-col">
        {page && (
          <AlertQueueSummaryBar summary={page.summary} count={page.count} asOf={page.asOf} />
        )}
        <div className="min-h-0 flex-1">
          <AlertMasterDetail
            mode="queue"
            rows={rows}
            asOf={page?.asOf}
            selectedId={store.selectedQueueAlertId}
            onSelect={store.selectQueueAlert}
            toolbar={toolbar}
            footer={footer}
            emptyState={
              queueLocationId == null ? <ScopeRequiredState what="queue" /> : <QueueEmptyState />
            }
            isInitialLoading={query.isPending && filterState != null}
            error={query.error}
            onRetry={() => void query.refetch()}
            renderDetail={({ row, onClose, onNext }) => (
              <AlertDetailPane row={row} onClose={onClose} onNext={onNext} asOf={page?.asOf} />
            )}
          />
        </div>
      </div>
    </AlertsScopeGate>
  );
};

export default AlertsQueuePage;
